import { Button, Flex, HStack, Pagination, Text } from '@chakra-ui/react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { useI18n } from 'src/i18n/I18nContext';

interface TablePaginationProps {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    loading?: boolean;
}

export default function TablePagination({ page, pageSize, total, onPageChange, loading = false }: TablePaginationProps) {
    const { t } = useI18n();
    const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, total);

    return (
        <Flex borderTopWidth='1px' borderColor='border' pt={3} mt={2} gap={3}
            direction={{ base: 'column', sm: 'row' }} align={{ base: 'stretch', sm: 'center' }} justify='space-between'
            aria-busy={loading}>
            <Text fontSize='sm' color='fg.muted' aria-live='polite'>
                {t('common.pagination.range', { start, end, total })}
            </Text>
            {total > pageSize && (
                <Pagination.Root count={total} pageSize={pageSize} page={page}
                    onPageChange={event => { if (!loading) onPageChange(event.page); }}
                    translations={{
                        rootLabel: t('common.pagination.label'),
                        prevTriggerLabel: t('common.pagination.previous'),
                        nextTriggerLabel: t('common.pagination.next'),
                    }}>
                    <HStack gap={2} justify='flex-end' flexWrap='wrap'>
                        <Pagination.PrevTrigger asChild>
                            <Button size='sm' variant='outline' disabled={loading || page <= 1}>
                                <BiChevronLeft />{t('common.pagination.previous')}
                            </Button>
                        </Pagination.PrevTrigger>
                        <Pagination.PageText fontSize='sm' color='fg.muted' whiteSpace='nowrap'
                            format={({ page, totalPages }) => t('common.pagination.page', { page, totalPages })} />
                        <Pagination.NextTrigger asChild>
                            <Button size='sm' variant='outline' disabled={loading || page >= Math.ceil(total / pageSize)}>
                                {t('common.pagination.next')}<BiChevronRight />
                            </Button>
                        </Pagination.NextTrigger>
                    </HStack>
                </Pagination.Root>
            )}
        </Flex>
    );
}
